"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

export const CAROUSEL_CONTAINER_CLASS =
  "scrollbar-hide flex select-none snap-x snap-mandatory items-stretch gap-3 overflow-x-auto overflow-y-hidden overscroll-x-contain scroll-smooth pb-1 cursor-grab active:cursor-grabbing";

export function useDragScroll(ref: React.RefObject<HTMLDivElement | null>) {
  const isPointerDown = useRef(false);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollStart = useRef(0);
  const pointerId = useRef(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) {
        return;
      }
      isPointerDown.current = true;
      isDragging.current = false;
      startX.current = e.clientX;
      scrollStart.current = el.scrollLeft;
      pointerId.current = e.pointerId;
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isPointerDown.current) {
        return;
      }
      const dx = e.clientX - startX.current;
      if (!isDragging.current) {
        if (Math.abs(dx) < 5) {
          return;
        }
        isDragging.current = true;
        el.setPointerCapture(pointerId.current);
        el.style.scrollSnapType = "none";
        el.style.scrollBehavior = "auto";
      }
      el.scrollLeft = scrollStart.current - dx;
    };

    const onPointerUp = () => {
      const wasDragging = isDragging.current;
      isPointerDown.current = false;
      isDragging.current = false;
      if (pointerId.current) {
        el.releasePointerCapture(pointerId.current);
      }
      requestAnimationFrame(() => {
        el.style.scrollSnapType = "";
        el.style.scrollBehavior = "";
      });
      if (wasDragging) {
        const preventClick = (ev: MouseEvent) => {
          ev.stopPropagation();
          ev.preventDefault();
        };
        el.addEventListener("click", preventClick, {
          capture: true,
          once: true,
        });
      }
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
    };
  }, [ref]);
}

export function CarouselArrows({
  scrollRef,
}: {
  scrollRef: React.RefObject<HTMLDivElement | null>;
}) {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, [scrollRef]);

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    el.addEventListener("scroll", updateArrows, { passive: true });
    const observer = new ResizeObserver(updateArrows);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      observer.disconnect();
    };
  }, [updateArrows, scrollRef]);

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const firstChild = el.firstElementChild as HTMLElement | null;
    const cardWidth = firstChild
      ? firstChild.offsetWidth + 12
      : el.clientWidth * 0.8;
    el.scrollBy({
      left: direction === "left" ? -cardWidth : cardWidth,
      behavior: "smooth",
    });
  };

  if (!canScrollLeft && !canScrollRight) {
    return null;
  }

  return (
    <div className="flex items-center gap-1.5">
      <button
        className={`border-border flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border transition-colors ${
          canScrollLeft
            ? "text-foreground hover:bg-muted"
            : "text-muted-foreground/30 cursor-default"
        }`}
        onClick={() => {
          scroll("left");
        }}
        disabled={!canScrollLeft}
      >
        <ChevronLeft size={16} />
      </button>
      <button
        className={`border-border flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border transition-colors ${
          canScrollRight
            ? "text-foreground hover:bg-muted"
            : "text-muted-foreground/30 cursor-default"
        }`}
        onClick={() => {
          scroll("right");
        }}
        disabled={!canScrollRight}
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
}
